import { AlertTriangle, Info, CalendarDays, BookOpen } from "lucide-react";
import { inr, cardCls, TIER_CLS, MONTHLY_RULE_LABEL } from "./frUtils";

const std = (o) => o?.standard_fee ?? o?.suggested_fee;
const MODE = { online_india: "Live Online · India", home: "Home Tuition", foreign: "Live Online · Foreign/NRI" };
const Stat = ({ label, value, sub, strong, testId }) => (
    <div className={`rounded-xl p-3 border min-w-0 ${strong ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-slate-50 border-slate-200"}`} data-testid={testId}>
        <div className={`text-[10px] uppercase tracking-wider font-bold ${strong ? "text-blue-200" : "text-slate-500"}`}>{label}</div>
        <div className="font-heading text-lg sm:text-xl font-bold tabular-nums mt-0.5 break-words">{value}</div>
        {sub && <div className={`text-[11px] ${strong ? "text-blue-100" : "text-slate-500"}`}>{sub}</div>}
    </div>
);
const range = (o) => (o.min_fee === o.max_fee ? inr(o.min_fee) : `${inr(o.min_fee)} – ${inr(o.max_fee)}`);

function Amounts({ r, testPrefix = "fr" }) {
    const ms = r.monthly_sessions;
    const pkg = r.package;
    const per = r.subjects.multiplier > 1 ? " / subject" : "";
    if (r.tuition_mode === "foreign") {
        return (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                <Stat label={`Per session${per}`} value={std(r.per_session) != null ? inr(std(r.per_session)) : range(r.per_session)} strong testId={`${testPrefix}-per-session`} sub={`${r.duration} min · INR`} />
                <Stat label={`Minimum / session${per}`} value={inr(r.per_session.min_fee)} testId={`${testPrefix}-min`} />
                <Stat label={`Maximum / session${per}`} value={inr(r.per_session.max_fee)} testId={`${testPrefix}-max`} />
            </div>
        );
    }
    if (r.mode === "package") {
        return (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                <Stat label={`${pkg.label || (r.calc_type === "crash" ? "Crash course" : "Yearly package")} fee`} value={inr(std(r.package_total) ?? r.package_total.min_fee)} strong testId={`${testPrefix}-package-total`} sub={`${pkg.sessions} sessions`} />
                <Stat label="Package min – max" value={range(r.package_total)} testId={`${testPrefix}-package-range`} />
                <Stat label="Per-session equivalent" value={inr(std(r.per_session) ?? r.per_session.min_fee)} testId={`${testPrefix}-per-session`} sub={r.subjects.note} />
            </div>
        );
    }
    return (
        <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                <Stat label="Recommended monthly" value={std(r.monthly) != null ? inr(std(r.monthly)) : range(r.monthly)} strong testId={`${testPrefix}-monthly-standard`}
                    sub={`${ms.count} sessions this month`} />
                <Stat label="Monthly min – max" value={range(r.monthly)} testId={`${testPrefix}-monthly`} />
                <Stat label={`Per session${per}`} value={std(r.per_session) != null ? inr(std(r.per_session)) : range(r.per_session)} testId={`${testPrefix}-per-session`}
                    sub={`range ${range(r.per_session)} · ${r.duration} min`} />
                <Stat label={`Minimum / session${per}`} value={inr(r.per_session.min_fee)} testId={`${testPrefix}-min`} />
                <Stat label={`Maximum / session${per}`} value={inr(r.per_session.max_fee)} testId={`${testPrefix}-max`} />
                {pkg && <Stat label={`Standard package${per}`} value={inr(pkg.standard)} testId={`${testPrefix}-package`}
                    sub={`${pkg.sessions} sessions · range ${pkg.min === pkg.max ? inr(pkg.min) : `${inr(pkg.min)}–${inr(pkg.max)}`}`} />}
            </div>
            <ul className="text-xs text-slate-600 space-y-1" data-testid={`${testPrefix}-basis`}>
                <li className="flex gap-1.5"><CalendarDays className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {r.sessions_per_week}/week · {ms.count} sessions/month · {ms.detail}{!ms.overridden && ` (${MONTHLY_RULE_LABEL[ms.rule]})`}</li>
                <li className="flex gap-1.5"><BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span data-testid={`${testPrefix}-subject-rule`}>{r.subjects.count} subject{r.subjects.count > 1 ? "s" : ""}: {r.subjects.note}</span></li>
                <li>Rate: {r.rate.label}{r.rate.factor !== 1 && ` · scaled from ${r.rate.base_duration}-min rate`} · {r.mode === "monthly" ? "monthly package pricing" : "per-session pricing"}</li>
            </ul>
        </>
    );
}

export default function ResultCard({ result: r }) {
    if (!r) {
        return (
            <div className={`${cardCls} text-sm text-slate-500 flex gap-2`} data-testid="fr-result-empty">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#1E3A8A]" />
                Enter the city, class, board and subjects, then tap <b className="text-slate-700">Generate Fee Recommendation</b>.
            </div>
        );
    }
    return (
        <div className={`${cardCls} space-y-4`} data-testid="fr-result">
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-900 text-white" data-testid="fr-result-mode">{MODE[r.tuition_mode] || MODE.online_india}</span>
                {r.country ? <span className="px-2.5 py-1 rounded-full border text-xs font-bold bg-violet-100 text-violet-800 border-violet-200" data-testid="fr-result-tier">{r.country.name}</span>
                    : r.tuition_mode !== "foreign" && <span className={`px-2.5 py-1 rounded-full border text-xs font-bold ${TIER_CLS[r.city.tier] || ""}`} data-testid="fr-result-tier">Tier {r.city.tier}</span>}
                {!r.country && <span className="font-semibold text-slate-900" data-testid="fr-result-city">{r.city.city || r.city.input}</span>}
                <span className="text-slate-300">·</span>
                <span className="font-semibold text-slate-900" data-testid="fr-result-category">{r.category?.name || "No category"}</span>
                {r.board?.applies && r.board.name && <><span className="text-slate-300">·</span><span className="font-semibold text-slate-900" data-testid="fr-result-board">{r.board.name}</span></>}
                {r.structure?.commerce_subjects && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-800" data-testid="fr-result-commerce">Commerce package · {r.structure.commerce_subjects} subject{r.structure.commerce_subjects > 1 ? "s" : ""}</span>}
            </div>
            {r.status !== "ok" ? (
                <>
                    <div className="flex gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="fr-result-missing">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {r.message}
                    </div>
                    {r.reference && (
                        <div className="rounded-xl border border-dashed border-slate-300 p-3 space-y-3" data-testid="fr-result-reference">
                            <div className="text-xs font-bold text-slate-600">{r.reference.note}</div>
                            <Amounts r={{ ...r, ...r.reference }} testPrefix="fr-ref" />
                        </div>
                    )}
                </>
            ) : (
                <>
                    <Amounts r={r} />
                    <div className="text-xs text-slate-500">Approved structure effective from {r.structure.effective_from}{r.structure.city_specific ? " · city-specific" : ""}{r.structure.grade_specific ? " · class-specific" : ""}</div>
                </>
            )}
        </div>
    );
}
